import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Plus, Edit, Trash2, Lock, CheckCircle, AlertCircle, Users, Copy, Eye, EyeOff, Send, Trophy, TrendingUp, Smile, Zap, Calendar, ChevronDown, ChevronRight, Link2, Archive, RefreshCw, Wrench } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DAYS_OF_WEEK = [
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
  { value: 7, label: "Sunday" }
];

// Compact Programme Type presets — mirrors parent-wizard pill colours so the
// visual identity stays consistent across admin and parent-facing surfaces.
const PROGRAMME_TYPE_PILLS = {
  competitive:   { label: "Competitive",   Icon: Trophy,      cls: "bg-amber-500/15 text-amber-300 border-amber-400/30" },
  development:   { label: "Development",   Icon: TrendingUp,  cls: "bg-cyan-500/15 text-cyan-300 border-cyan-400/30" },
  recreational:  { label: "Recreational",  Icon: Smile,       cls: "bg-emerald-500/15 text-emerald-300 border-emerald-400/30" },
  standard:      { label: "Standard",      Icon: Calendar,    cls: "bg-slate-500/15 text-slate-300 border-slate-400/30" },
  adhoc:         { label: "Ad-Hoc",        Icon: Zap,         cls: "bg-blue-500/15 text-blue-300 border-blue-400/30" },
  girls:         { label: "Girls",         Icon: Users,       cls: "bg-rose-500/15 text-rose-300 border-rose-400/30" },
};

// A term is considered "archived" when it's finalised AND its end_date is in the past.
const isTermArchived = (term) => {
  if (!term?.is_finalized) return false;
  if (!term?.end_date) return false;
  try {
    return new Date(term.end_date) < new Date(new Date().toDateString());
  } catch {
    return false;
  }
};

export default function AcademyTermManagement({ user }) {
  const [terms, setTerms] = useState([]);
  const [selectedTerm, setSelectedTerm] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [rateTables, setRateTables] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  // Term dialog
  const [showTermDialog, setShowTermDialog] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  const [viewOnlyMode, setViewOnlyMode] = useState(false); // NEW: View-only mode for finalized terms
  const [termName, setTermName] = useState("");
  const [termDescription, setTermDescription] = useState("");
  const [termProgrammeType, setTermProgrammeType] = useState("");
  const [termTargetAudience, setTermTargetAudience] = useState("");
  const [termHighlights, setTermHighlights] = useState(["", "", ""]);
  const [termStartDate, setTermStartDate] = useState("");
  const [termDurationValue, setTermDurationValue] = useState(10);
  const [termDurationUnit, setTermDurationUnit] = useState("weeks");
  const [termRateTableId, setTermRateTableId] = useState("");
  const [termBreaks, setTermBreaks] = useState([]); // [{start_date, end_date, reason}]
  const [sessionDatesData, setSessionDatesData] = useState(null); // per-weekday occurrence dates for cancel picker
  const [loadingSessionDates, setLoadingSessionDates] = useState(false);
  const [cancelConfirm, setCancelConfirm] = useState(null); // {date, day_name, affected_paid_players, total_credit}
  const [cancelBusy, setCancelBusy] = useState(false);
  const [showPriceAudit, setShowPriceAudit] = useState(false);
  const [priceAudit, setPriceAudit] = useState(null);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditBusy, setAuditBusy] = useState(false);
  const [endDateMode, setEndDateMode] = useState("auto"); // "auto" or "manual"
  const [manualEndDate, setManualEndDate] = useState("");

  // Session dialog
  const [showSessionDialog, setShowSessionDialog] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [sessionDay, setSessionDay] = useState(1);
  const [sessionStartTime, setSessionStartTime] = useState("17:30");
  const [sessionEndTime, setSessionEndTime] = useState("18:30");
  const [sessionType, setSessionType] = useState("training");
  const [sessionLocation, setSessionLocation] = useState("Dempsey Field");
  const [sessionCustomLocation, setSessionCustomLocation] = useState("");
  const [sessionPrice, setSessionPrice] = useState("");
  const [sessionDescription, setSessionDescription] = useState("");
  
  // NEW: Multiple Age Groups Support
  const [sessionAgeGroups, setSessionAgeGroups] = useState([]);
  const [newAgeGroup, setNewAgeGroup] = useState({ name: "", minAge: "", maxAge: "", gender: "mixed" });
  
  // Notify parents state
  const [notifyingTerm, setNotifyingTerm] = useState(null);

  // Copy Term dialog state
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [sourceTerm, setSourceTerm] = useState(null);
  const [copyNewName, setCopyNewName] = useState("");
  const [copyNewStartDate, setCopyNewStartDate] = useState("");
  const [copySessions, setCopySessions] = useState(true);
  const [copyLoading, setCopyLoading] = useState(false);

  // Archive accordion + "Copy sessions from another term" (bulk import) state
  const [showArchived, setShowArchived] = useState(false);
  const [copySessionsFromId, setCopySessionsFromId] = useState("");
  const [copySessionsFromLoading, setCopySessionsFromLoading] = useState(false);
  const [copySessionsClearFirst, setCopySessionsClearFirst] = useState(false);
  
  // Legacy single age group (for backwards compatibility)
  const [sessionAgeGroupName, setSessionAgeGroupName] = useState("");
  const [sessionMinAge, setSessionMinAge] = useState("");
  const [sessionMaxAge, setSessionMaxAge] = useState("");
  const [sessionGender, setSessionGender] = useState("mixed");
  
  const [createForBothGenders, setCreateForBothGenders] = useState(false);
  const [copyToMultipleDays, setCopyToMultipleDays] = useState(false);
  const [selectedDays, setSelectedDays] = useState([]);
  
  // Helper to add age group
  const addAgeGroup = () => {
    if (!newAgeGroup.name || !newAgeGroup.minAge || !newAgeGroup.maxAge) {
      toast.error("Please fill in age group name, min age, and max age");
      return;
    }
    if (parseInt(newAgeGroup.minAge) > parseInt(newAgeGroup.maxAge)) {
      toast.error("Min age cannot be greater than max age");
      return;
    }
    setSessionAgeGroups([...sessionAgeGroups, { ...newAgeGroup, id: Date.now() }]);
    setNewAgeGroup({ name: "", minAge: "", maxAge: "", gender: "mixed" });
    toast.success(`Added ${newAgeGroup.name}`);
  };
  
  const removeAgeGroup = (id) => {
    setSessionAgeGroups(sessionAgeGroups.filter(ag => ag.id !== id));
  };

  const isAdmin = ["convenor", "captain"].includes(user?.role);

  // Get the rate table for the selected term
  const selectedTermRateTable = rateTables.find(rt => rt.id === selectedTerm?.rate_table_id);
  const isVolumePricing = selectedTermRateTable?.pricing_mode === "volume";
  const volumeTiers = selectedTermRateTable?.volume_pricing_tiers || [];
  const firstTierPrice = volumeTiers.find(t => t.min_sessions === 1)?.price_per_session || volumeTiers[0]?.price_per_session || "";
  const isMaxCapacity = selectedTermRateTable?.pricing_mode === "max_capacity";
  const isProgressive = selectedTermRateTable?.pricing_mode === "progressive";
  const mcPrice = selectedTermRateTable?.max_capacity_price_per_session;
  const mcMaxSessions = selectedTermRateTable?.max_sessions_per_week || 1;
  // A rate-table-driven mode supplies the price — the manual per-session field is optional/locked.
  const isRateDriven = isVolumePricing || isMaxCapacity;
  // The per-session price the rate table implies (string), used to auto-fill the locked field.
  const rateTablePriceStr = isVolumePricing && firstTierPrice
    ? firstTierPrice.toString()
    : (isMaxCapacity && mcPrice ? mcPrice.toString() : "");
  // Label shown on a session card / summary — reflects the rate table when one drives pricing.
  const sessionPriceLabel = (session) => {
    if (isVolumePricing && firstTierPrice) return `$${firstTierPrice}/session`;
    if (isMaxCapacity && mcPrice) {
      const wk = (parseFloat(mcPrice) * mcMaxSessions).toFixed(2);
      return `$${mcPrice}/session · $${wk}/week (×${mcMaxSessions})`;
    }
    return `$${session?.price ?? 0}/session`;
  };

  useEffect(() => {
    if (isAdmin) {
      fetchRateTables();
      fetchTerms();
      fetchLocations();
    }
  }, [isAdmin]);

  const fetchRateTables = async () => {
    try {
      const response = await axios.get(`${API}/academy/rate-tables`, { withCredentials: true });
      setRateTables(response.data);
    } catch (error) {
      console.error("Error fetching rate tables:", error);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await axios.get(`${API}/academy/locations`, { withCredentials: true });
      setLocations(response.data);
      
      // If no locations exist, seed defaults
      if (response.data.length === 0) {
        await axios.post(`${API}/academy/locations/seed-defaults`, {}, { withCredentials: true });
        const refreshed = await axios.get(`${API}/academy/locations`, { withCredentials: true });
        setLocations(refreshed.data);
      }
    } catch (error) {
      console.error("Error fetching locations:", error);
    }
  };

  const fetchTerms = async () => {
    try {
      const response = await axios.get(`${API}/academy/terms`, { withCredentials: true });
      setTerms(response.data);
      if (response.data.length > 0 && !selectedTerm) {
        setSelectedTerm(response.data[0]);
        fetchSessions(response.data[0].id);
      }
    } catch (error) {
      console.error("Error fetching terms:", error);
      toast.error("Failed to load terms");
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async (termId) => {
    try {
      const response = await axios.get(`${API}/academy/terms/${termId}/sessions`, { withCredentials: true });
      setSessions(response.data);
    } catch (error) {
      console.error("Error fetching sessions:", error);
      toast.error("Failed to load sessions");
    }
  };

  const openCreateTermDialog = () => {
    setEditingTerm(null);
    setTermName("");
    setTermDescription("");
    setTermProgrammeType("");
    setTermTargetAudience("");
    setTermHighlights(["", "", ""]);
    setTermStartDate("");
    setTermDurationValue(10);
    setTermDurationUnit("weeks");
    setTermRateTableId("");
    setTermBreaks([]);
    setEndDateMode("auto");
    setManualEndDate("");
    setShowTermDialog(true);
  };

  const openEditTermDialog = async (term, forceViewOnly = false) => {
    // Finalized terms are editable if no paid enrollments exist
    // If paid enrollments exist, show warning but allow force-edit for global admin
    let isViewOnly = forceViewOnly;
    let paidCount = 0;
    if (term?.is_finalized && !forceViewOnly) {
      try {
        const res = await axios.get(`${API}/academy/terms/${term.id}/enrollment-count`, { withCredentials: true });
        paidCount = res.data?.paid_count || 0;
        if (paidCount > 0) {
          // Show confirmation dialog for force-edit
          const confirmed = window.confirm(
            `This term has ${paidCount} paid enrollment(s).\n\n` +
            `Editing term dates may affect enrolled parents. ` +
            `You may need to notify them of any changes.\n\n` +
            `Do you want to force-edit this term?`
          );
          isViewOnly = !confirmed;
          if (confirmed) {
            toast.warning(`Force-editing enabled. ${paidCount} paid enrollment(s) exist — remember to notify affected parents if dates change.`, { duration: 8000 });
          }
        }
      } catch {
        isViewOnly = false;
      }
    }
    setViewOnlyMode(isViewOnly);
    setEditingTerm(term);
    setTermName(term?.name || "");
    setTermDescription(term?.description || "");
    setTermProgrammeType(term?.programme_type || "");
    setTermTargetAudience(term?.target_audience || "");
    {
      const savedHl = Array.isArray(term?.highlights) ? term.highlights : [];
      setTermHighlights([savedHl[0] || "", savedHl[1] || "", savedHl[2] || ""]);
    }
    setTermStartDate(term?.start_date || "");
    setTermDurationValue(term?.duration_value || term?.duration_weeks || 10);
    setTermDurationUnit(term?.duration_unit || "weeks");
    setTermRateTableId(term?.rate_table_id || "");
    setTermBreaks(term?.term_breaks || []);
    setEndDateMode(term?.end_date_mode || "auto");
    setManualEndDate(term?.end_date || "");
    setShowTermDialog(true);
    // Load per-weekday session dates for the single-date cancellation picker
    setSessionDatesData(null);
    if (term?.id) loadSessionDates(term.id);
  };

  const loadSessionDates = async (termId) => {
    setLoadingSessionDates(true);
    try {
      const res = await axios.get(`${API}/academy/admin/terms/${termId}/session-dates`, { withCredentials: true });
      setSessionDatesData(res.data);
    } catch (e) {
      setSessionDatesData(null);
    } finally {
      setLoadingSessionDates(false);
    }
  };

  const openPriceAudit = async () => {
    setShowPriceAudit(true);
    setAuditLoading(true);
    setPriceAudit(null);
    try {
      const res = await axios.get(`${API}/academy/admin/price-audit`, { withCredentials: true });
      setPriceAudit(res.data);
    } catch (e) {
      toast.error("Failed to load price audit");
    } finally {
      setAuditLoading(false);
    }
  };

  const syncAudit = async (termId) => {
    setAuditBusy(true);
    try {
      const res = await axios.post(`${API}/academy/admin/price-audit/sync`, termId ? { term_id: termId } : {}, { withCredentials: true });
      toast.success(res.data.message);
      const r = await axios.get(`${API}/academy/admin/price-audit`, { withCredentials: true });
      setPriceAudit(r.data);
      if (selectedTerm?.id) fetchSessions(selectedTerm.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setAuditBusy(false);
    }
  };

  const handleDateChipClick = async (dateObj) => {
    if (!editingTerm?.id || viewOnlyMode) return;
    if (dateObj.cancelled) {
      // Restore
      setCancelBusy(true);
      try {
        const res = await axios.post(`${API}/academy/admin/terms/${editingTerm.id}/restore-session-date`, { date: dateObj.date }, { withCredentials: true });
        toast.success(`Session restored for ${dateObj.date}`);
        if (res.data?.warning) toast.warning(res.data.warning, { duration: 9000 });
        await loadSessionDates(editingTerm.id);
      } catch (e) {
        toast.error(e.response?.data?.detail || "Failed to restore date");
      } finally {
        setCancelBusy(false);
      }
      return;
    }
    // Preview affected paid players first
    setCancelBusy(true);
    try {
      const res = await axios.post(`${API}/academy/admin/terms/${editingTerm.id}/cancel-session-date`, { date: dateObj.date, preview: true }, { withCredentials: true });
      if ((res.data?.affected_count || 0) === 0) {
        // No paid players — cancel immediately
        await axios.post(`${API}/academy/admin/terms/${editingTerm.id}/cancel-session-date`, { date: dateObj.date, issue_credits: false }, { withCredentials: true });
        toast.success(`Session on ${res.data.day_name} ${dateObj.date} cancelled`);
        await loadSessionDates(editingTerm.id);
      } else {
        // Show confirm dialog with affected paid players + credits
        setCancelConfirm({ date: dateObj.date, ...res.data });
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel date");
    } finally {
      setCancelBusy(false);
    }
  };

  const confirmCancelDate = async (issueCredits) => {
    if (!editingTerm?.id || !cancelConfirm) return;
    setCancelBusy(true);
    try {
      const res = await axios.post(`${API}/academy/admin/terms/${editingTerm.id}/cancel-session-date`,
        { date: cancelConfirm.date, issue_credits: issueCredits, reason: "Session cancelled" }, { withCredentials: true });
      toast.success(issueCredits
        ? `Cancelled ${cancelConfirm.date}. ${res.data.credits_issued} credit(s) issued ($${res.data.total_credit}).`
        : `Cancelled ${cancelConfirm.date} (no credits issued).`);
      setCancelConfirm(null);
      await loadSessionDates(editingTerm.id);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to cancel date");
    } finally {
      setCancelBusy(false);
    }
  };

  // Calculate end date based on start date, duration, and breaks
  const calculateEndDate = () => {
    if (!termStartDate || !termDurationValue) return "";
    
    const startDate = new Date(termStartDate);
    const trainingWeeks = parseInt(termDurationValue);
    
    // Count total break weeks
    let breakWeeks = 0;
    termBreaks.forEach(brk => {
      if (brk.start_date && brk.end_date) {
        const breakStart = new Date(brk.start_date);
        const breakEnd = new Date(brk.end_date);
        const diffTime = Math.abs(breakEnd - breakStart);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        breakWeeks += Math.ceil(diffDays / 7);
      }
    });
    
    // Total calendar weeks = training weeks + break weeks
    const totalCalendarWeeks = trainingWeeks + breakWeeks;
    
    // Calculate end date
    const endDate = new Date(startDate);
    endDate.setDate(endDate.getDate() + (totalCalendarWeeks * 7) - 1);
    
    return endDate.toISOString().split('T')[0];
  };

  const calculatedEndDate = calculateEndDate();
  const breakWeeksCount = termBreaks.reduce((total, brk) => {
    if (brk.start_date && brk.end_date) {
      const breakStart = new Date(brk.start_date);
      const breakEnd = new Date(brk.end_date);
      const diffTime = Math.abs(breakEnd - breakStart);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return total + Math.ceil(diffDays / 7);
    }
    return total;
  }, 0);

  const handleTermSubmit = async () => {
    if (!termName.trim() || !termStartDate) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const payload = {
        name: termName,
        description: termDescription,
        programme_type: termProgrammeType || null,
        target_audience: termTargetAudience,
        highlights: termHighlights.map(h => (h || "").trim()).filter(Boolean).slice(0, 3),
        start_date: termStartDate,
        duration_value: parseInt(termDurationValue),
        duration_unit: termDurationUnit,
        rate_table_id: (termRateTableId && termRateTableId !== "none") ? termRateTableId : null,
        term_breaks: termBreaks.filter(b => b.start_date && b.end_date),
        end_date_mode: endDateMode,
        end_date: endDateMode === "manual" ? manualEndDate : calculatedEndDate
      };

      if (editingTerm) {
        // Include force_edit flag if editing a finalized term
        if (editingTerm.is_finalized) {
          payload.force_edit = true;
        }
        await axios.put(`${API}/academy/terms/${editingTerm.id}`, payload, { withCredentials: true });
        toast.success("Term updated successfully");
      } else {
        await axios.post(`${API}/academy/terms`, payload, { withCredentials: true });
        toast.success("Term created successfully");
      }

      setShowTermDialog(false);
      fetchTerms();
    } catch (error) {
      console.error("Error saving term:", error);
      toast.error(error.response?.data?.detail || "Failed to save term");
    }
  };

  const openCopyTermDialog = (term) => {
    setSourceTerm(term);
    setCopyNewName(`Copy of ${term.name}`);
    // Default new start = day after source end
    let defaultStart = "";
    if (term.end_date) {
      try {
        const d = new Date(term.end_date);
        d.setDate(d.getDate() + 1);
        defaultStart = d.toISOString().split("T")[0];
      } catch { /* noop */ }
    }
    setCopyNewStartDate(defaultStart);
    setCopySessions(true);
    setShowCopyDialog(true);
  };

  const handleCopyTermSubmit = async () => {
    if (!sourceTerm || !copyNewName.trim() || !copyNewStartDate) {
      toast.error("Please fill in the new term name and start date");
      return;
    }
    try {
      setCopyLoading(true);
      const res = await axios.post(
        `${API}/academy/terms/${sourceTerm.id}/duplicate`,
        {
          name: copyNewName.trim(),
          start_date: copyNewStartDate,
          copy_sessions: copySessions,
        },
        { withCredentials: true }
      );
      const sessionsCount = res.data?.sessions_copied || 0;
      toast.success(
        copySessions
          ? `Term duplicated with ${sessionsCount} session${sessionsCount === 1 ? "" : "s"} — draft & hidden by default. Edit and finalize when ready.`
          : "Term duplicated (no sessions copied) — draft & hidden by default."
      );
      setShowCopyDialog(false);
      setSourceTerm(null);
      fetchTerms();
    } catch (error) {
      console.error("Error duplicating term:", error);
      toast.error(error.response?.data?.detail || "Failed to duplicate term");
    } finally {
      setCopyLoading(false);
    }
  };

  const handleDeleteTerm = async (termId) => {
    if (!window.confirm("Are you sure you want to delete this term? This will also delete all associated sessions.")) {
      return;
    }

    try {
      await axios.delete(`${API}/academy/terms/${termId}`, { withCredentials: true });
      toast.success("Term deleted successfully");
      if (selectedTerm?.id === termId) {
        setSelectedTerm(null);
        setSessions([]);
      }
      fetchTerms();
    } catch (error) {
      console.error("Error deleting term:", error);
      toast.error(error.response?.data?.detail || "Failed to delete term");
    }
  };

  const handleFinalizeTerm = async (termId) => {
    if (!window.confirm("Finalize this term? Once finalized, no changes can be made. This action cannot be undone.")) {
      return;
    }

    try {
      await axios.post(`${API}/academy/terms/${termId}/finalize`, {}, { withCredentials: true });
      toast.success("Term finalized successfully");
      fetchTerms();
      if (selectedTerm?.id === termId) {
        fetchSessions(termId);
      }
    } catch (error) {
      console.error("Error finalizing term:", error);
      toast.error(error.response?.data?.detail || "Failed to finalize term");
    }
  };

  const [resyncingTerm, setResyncingTerm] = useState(null);
  const handleRegenerateSessions = async (termId) => {
    if (!window.confirm("Re-sync sessions for this term? This creates any missing training sessions and refreshes rosters from current enrolments. Existing sessions with attendance are preserved.")) {
      return;
    }
    setResyncingTerm(termId);
    try {
      const res = await axios.post(
        `${API}/academy/terms/${termId}/regenerate-sessions`,
        {},
        { withCredentials: true }
      );
      const d = res.data || {};
      toast.success(
        `Sessions re-synced — ${d.sessions_created ?? 0} created, ${d.sessions_updated ?? 0} updated with current enrolments.`,
        { duration: 6000 }
      );
      if (selectedTerm?.id === termId) fetchSessions(termId);
    } catch (error) {
      console.error("Error regenerating sessions:", error);
      toast.error(error.response?.data?.detail || "Failed to re-sync sessions");
    } finally {
      setResyncingTerm(null);
    }
  };

  // ── "Fix this term" — dry-run preview → apply (repair links + regenerate) ──
  const [fixTermData, setFixTermData] = useState(null); // { termId, termName, ...dryRunResult }
  const [fixTermLoading, setFixTermLoading] = useState(null); // termId while previewing
  const [fixTermApplying, setFixTermApplying] = useState(false);

  const openFixTerm = async (termId, termName) => {
    setFixTermLoading(termId);
    try {
      const res = await axios.post(
        `${API}/academy/admin/terms/${termId}/fix-term`,
        { dry_run: true },
        { withCredentials: true }
      );
      setFixTermData({ termId, termName, ...res.data });
    } catch (error) {
      console.error("fix-term dry run failed:", error);
      toast.error(error.response?.data?.detail || "Failed to load repair preview");
    } finally {
      setFixTermLoading(null);
    }
  };

  const applyFixTerm = async () => {
    if (!fixTermData?.termId) return;
    setFixTermApplying(true);
    try {
      const res = await axios.post(
        `${API}/academy/admin/terms/${fixTermData.termId}/fix-term`,
        { dry_run: false },
        { withCredentials: true }
      );
      const d = res.data || {};
      toast.success(d.message || "Term repaired successfully", { duration: 8000 });
      setFixTermData({ ...fixTermData, ...d, applied: true });
      if (selectedTerm?.id === fixTermData.termId) fetchSessions(fixTermData.termId);
    } catch (error) {
      console.error("fix-term apply failed:", error);
      toast.error(error.response?.data?.detail || "Failed to apply term repair");
    } finally {
      setFixTermApplying(false);
    }
  };

  const toggleTermVisibility = async (termId, currentlyHidden) => {
    try {
      await axios.put(
        `${API}/academy/terms/${termId}/visibility`, 
        { is_hidden: !currentlyHidden }, 
        { withCredentials: true }
      );
      toast.success(currentlyHidden ? "Term is now visible for registrations" : "Term is now hidden from registrations");
      fetchTerms();
    } catch (error) {
      console.error("Error toggling term visibility:", error);
      toast.error(error.response?.data?.detail || "Failed to update term visibility");
    }
  };

  // Notify parents about enrollment open
  const handleNotifyEnrollmentOpen = async (termId, termName) => {
    if (!window.confirm(`Send Telegram notification to all parents that enrollment is open for "${termName}"?`)) {
      return;
    }
    
    setNotifyingTerm(termId);
    try {
      const response = await axios.post(
        `${API}/academy/terms/${termId}/notify-enrollment-open`, 
        {}, 
        { withCredentials: true }
      );
      toast.success(`Notification sent to ${response.data.sent_count} parents!`);
    } catch (error) {
      console.error("Error notifying parents:", error);
      toast.error(error.response?.data?.detail || "Failed to send notifications");
    } finally {
      setNotifyingTerm(null);
    }
  };

  // Handler for changing rate table on finalized terms
  const handleChangeRateTable = async () => {
    if (!editingTerm?.id || !termRateTableId || termRateTableId === "none") {
      toast.error("Please select a rate table");
      return;
    }

    const confirmed = window.confirm(
      "Are you sure you want to change the rate table for this finalized term?\n\n" +
      "⚠️ This only affects NEW registrations.\n" +
      "✅ Existing payments and enrollments are NOT affected."
    );

    if (!confirmed) return;

    try {
      await axios.patch(
        `${API}/academy/terms/${editingTerm.id}/rate-table`,
        { rate_table_id: termRateTableId },
        { withCredentials: true }
      );
      toast.success("Rate table updated successfully! New registrations will use the updated pricing.");
      setShowTermDialog(false);
      fetchTerms();
    } catch (error) {
      console.error("Error changing rate table:", error);
      toast.error(error.response?.data?.detail || "Failed to change rate table");
    }
  };

  const openCreateSessionDialog = () => {
    if (!selectedTerm) {
      toast.error("Please select a term first");
      return;
    }
    // Allow adding sessions even to finalized terms
    setEditingSession(null);
    setSessionDay(1);
    setSessionStartTime("17:30");
    setSessionEndTime("18:30");
    setSessionType("training");
    setSessionLocation("Dempsey Field");
    setSessionCustomLocation("");
    // Auto-populate price from the rate table when one drives pricing (volume/max-capacity)
    setSessionPrice(rateTablePriceStr || "");
    setSessionDescription("");
    setSessionAgeGroupName("");
    setSessionMinAge("");
    setSessionMaxAge("");
    setSessionGender("mixed");
    setSessionAgeGroups([]);
    setNewAgeGroup({ name: "", minAge: "", maxAge: "", gender: "mixed" });
    setCreateForBothGenders(false);
    setCopyToMultipleDays(false);
    setSelectedDays([]);
    setShowSessionDialog(true);
  };

  const openEditSessionDialog = (session) => {
    // Allow editing sessions even in finalized terms
    setEditingSession(session);
    setSessionDay(session?.day_of_week || 1);
    setSessionStartTime(session?.start_time || "17:30");
    setSessionEndTime(session?.end_time || "18:30");
    setSessionType(session?.session_type || "training");
    
    // Check if saved location is in database locations list
    const savedLocation = session?.location || "Dempsey Field";
    const locationNames = locations.map(loc => loc.name);
    if (locationNames.includes(savedLocation)) {
      setSessionLocation(savedLocation);
      setSessionCustomLocation("");
    } else {
      setSessionLocation("Other");
      setSessionCustomLocation(savedLocation);
    }
    
    setSessionPrice(isRateDriven ? (rateTablePriceStr || session?.price?.toString() || "") : (session?.price?.toString() || ""));
    setSessionDescription(session?.description || "");
    setSessionAgeGroupName(session?.age_group_name || "");
    setSessionMinAge(session?.min_age?.toString() || "");
    setSessionMaxAge(session?.max_age?.toString() || "");
    setSessionGender(session?.gender || "mixed");
    setCreateForBothGenders(false);
    setCopyToMultipleDays(false);
    setSelectedDays([]);
    setShowSessionDialog(true);
  };

  const openCopySessionDialog = (session) => {
    // Allow copying sessions even in finalized terms
    // Copy session data but treat as NEW session (not editing)
    setEditingSession(null);
    setSessionDay(session?.day_of_week || 1);
    setSessionStartTime(session?.start_time || "17:30");
    setSessionEndTime(session?.end_time || "18:30");
    setSessionType(session?.session_type || "training");
    
    // Check if saved location is in database locations list
    const savedLocation = session?.location || "Dempsey Field";
    const locationNames = locations.map(loc => loc.name);
    if (locationNames.includes(savedLocation)) {
      setSessionLocation(savedLocation);
      setSessionCustomLocation("");
    } else {
      setSessionLocation("Other");
      setSessionCustomLocation(savedLocation);
    }
    
    // Auto-populate price from the rate table when one drives pricing, else keep copied session price
    setSessionPrice(isRateDriven ? (rateTablePriceStr || session?.price?.toString() || "") : (session?.price?.toString() || ""));
    setSessionDescription(session?.description || "");
    setSessionAgeGroupName(session?.age_group_name || "");
    setSessionMinAge(session?.min_age?.toString() || "");
    setSessionMaxAge(session?.max_age?.toString() || "");
    setSessionGender(session?.gender || "mixed");
    setCreateForBothGenders(false);
    setCopyToMultipleDays(true); // Enable multi-day by default when copying
    setSelectedDays([]); // Start with no extra days selected
    setShowSessionDialog(true);
    toast.info("Session copied - select days and save");
  };

  const handleSessionSubmit = async () => {
    // When the term's rate table drives pricing (volume / max-capacity), the per-session
    // price comes from the rate table — the manual field is optional. Otherwise it's required.
    let effectivePrice = sessionPrice;
    if (isRateDriven) {
      if (rateTablePriceStr) effectivePrice = rateTablePriceStr;
      else if (!effectivePrice) effectivePrice = "0";
    } else if (!sessionPrice || parseFloat(sessionPrice) <= 0) {
      toast.error("Please enter a valid price");
      return;
    }

    // Determine final location value
    const finalLocation = sessionLocation === "Other" ? sessionCustomLocation : sessionLocation;
    
    if (!finalLocation) {
      toast.error("Please select or enter a location");
      return;
    }

    try {
      if (editingSession) {
        // Editing existing session - use legacy fields
        const payload = {
          day_of_week: parseInt(sessionDay),
          start_time: sessionStartTime,
          end_time: sessionEndTime,
          session_type: sessionType,
          location: finalLocation,
          price: parseFloat(effectivePrice) || 0,
          description: sessionDescription,
          age_group_name: sessionAgeGroupName || null,
          min_age: sessionMinAge ? parseInt(sessionMinAge) : null,
          max_age: sessionMaxAge ? parseInt(sessionMaxAge) : null,
          gender: sessionGender
        };
        await axios.put(`${API}/academy/session-templates/${editingSession.id}`, payload, { withCredentials: true });
        toast.success("Session updated successfully");
      } else {
        // Creating new sessions
        
        // Determine which days to create sessions for
        let daysToCreate = [parseInt(sessionDay)];
        if (copyToMultipleDays && selectedDays.length > 0) {
          daysToCreate = [...new Set([...daysToCreate, ...selectedDays])];
        }
        
        // Build list of age groups to create
        let ageGroupsToCreate = [];
        
        if (sessionAgeGroups.length > 0) {
          // Using new multiple age groups feature
          ageGroupsToCreate = sessionAgeGroups.map(ag => ({
            age_group_name: ag.name,
            min_age: parseInt(ag.minAge),
            max_age: parseInt(ag.maxAge),
            gender: ag.gender
          }));
        } else {
          // Using legacy single age group (or no age group)
          ageGroupsToCreate = [{
            age_group_name: sessionAgeGroupName || null,
            min_age: sessionMinAge ? parseInt(sessionMinAge) : null,
            max_age: sessionMaxAge ? parseInt(sessionMaxAge) : null,
            gender: sessionGender
          }];
        }
        
        // If "Create for Both Genders" is checked, add opposite gender for each age group
        // BUT avoid duplicates if both genders already exist for same age group
        if (createForBothGenders) {
          const expanded = [];
          const existingKeys = new Set(); // Track what we've already added
          
          for (const ag of ageGroupsToCreate) {
            const baseKey = `${ag.age_group_name}-${ag.min_age}-${ag.max_age}`;
            
            if (ag.gender === "male") {
              // Add boys if not already added
              if (!existingKeys.has(`${baseKey}-male`)) {
                expanded.push(ag);
                existingKeys.add(`${baseKey}-male`);
              }
              // Add girls if not already added
              if (!existingKeys.has(`${baseKey}-female`)) {
                expanded.push({ ...ag, gender: "female" });
                existingKeys.add(`${baseKey}-female`);
              }
            } else if (ag.gender === "female") {
              // Add boys if not already added
              if (!existingKeys.has(`${baseKey}-male`)) {
                expanded.push({ ...ag, gender: "male" });
                existingKeys.add(`${baseKey}-male`);
              }
              // Add girls if not already added
              if (!existingKeys.has(`${baseKey}-female`)) {
                expanded.push(ag);
                existingKeys.add(`${baseKey}-female`);
              }
            } else {
              // Mixed - just add once
              if (!existingKeys.has(`${baseKey}-mixed`)) {
                expanded.push(ag);
                existingKeys.add(`${baseKey}-mixed`);
              }
            }
          }
          ageGroupsToCreate = expanded;
        }
        
        // Create sessions for all combinations of days × age groups
        let createdCount = 0;
        for (const day of daysToCreate) {
          for (const ag of ageGroupsToCreate) {
            const payload = {
              day_of_week: day,
              start_time: sessionStartTime,
              end_time: sessionEndTime,
              session_type: sessionType,
              location: finalLocation,
              price: parseFloat(effectivePrice) || 0,
              description: sessionDescription,
              age_group_name: ag.age_group_name,
              min_age: ag.min_age,
              max_age: ag.max_age,
              gender: ag.gender
            };
            await axios.post(`${API}/academy/terms/${selectedTerm.id}/sessions`, payload, { withCredentials: true });
            createdCount++;
          }
        }
        
        if (createdCount === 1) {
          toast.success("Session created successfully");
        } else {
          toast.success(`🎉 ${createdCount} sessions created successfully!`);
        }
      }

      // Reset age groups after successful creation
      setSessionAgeGroups([]);
      setCreateForBothGenders(false);
      setCopyToMultipleDays(false);
      setSelectedDays([]);
      
      setShowSessionDialog(false);
      fetchSessions(selectedTerm.id);
    } catch (error) {
      console.error("Error saving session:", error);
      toast.error(error.response?.data?.detail || "Failed to save session");
    }
  };

  const handleDeleteSession = async (sessionId, enrollmentCount = 0) => {
    if (enrollmentCount > 0) {
      toast.error(`Cannot delete - ${enrollmentCount} player(s) enrolled in this session`);
      return;
    }
    
    if (!window.confirm("Are you sure you want to delete this session? This cannot be undone.")) {
      return;
    }

    try {
      await axios.delete(`${API}/academy/session-templates/${sessionId}`, { withCredentials: true });
      toast.success("Session deleted successfully");
      fetchSessions(selectedTerm.id);
    } catch (error) {
      console.error("Error deleting session:", error);
      toast.error(error.response?.data?.detail || "Failed to delete session");
    }
  };

  const handleFinalizeSession = async (sessionId, silent = false) => {
    if (!silent && !window.confirm("Finalize this session? It will become visible for enrollment.")) {
      return;
    }

    try {
      await axios.post(`${API}/academy/session-templates/${sessionId}/finalize`, {}, { withCredentials: true });
      if (!silent) {
        toast.success("Session finalized and now available for enrollment");
      }
      fetchSessions(selectedTerm.id);
    } catch (error) {
      console.error("Error finalizing session:", error);
      if (!silent) {
        toast.error(error.response?.data?.detail || "Failed to finalize session");
      }
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#0a0f1a] p-6">
        <Card className="max-w-2xl mx-auto p-8 text-center">
          <h2 className="text-2xl font-bold text-red-400 mb-4">Access Denied</h2>
          <p className="text-slate-400">Only administrators can manage terms and sessions.</p>
        </Card>
      </div>
    );
  }

  const getRateTableName = (rateTableId) => {
    const table = rateTables.find(t => t.id === rateTableId);
    return table ? table.name : "No rate table assigned";
  };

  // Handler — copy sessions from another term into the currently selected term.
  const handleCopySessionsFromTerm = async () => {
    if (!selectedTerm || !copySessionsFromId) {
      toast.error("Pick a source term to copy sessions from");
      return;
    }
    if (copySessionsFromId === selectedTerm.id) {
      toast.error("Source and target must be different terms");
      return;
    }
    if (copySessionsClearFirst && !window.confirm(
      `This will DELETE all existing sessions on "${selectedTerm.name}" first, then copy from the source term. Continue?`
    )) return;
    try {
      setCopySessionsFromLoading(true);
      const res = await axios.post(
        `${API}/academy/terms/${selectedTerm.id}/copy-sessions-from/${copySessionsFromId}`,
        { clear_existing: copySessionsClearFirst },
        { withCredentials: true }
      );
      toast.success(res.data?.message || "Sessions copied");
      setCopySessionsFromId("");
      setCopySessionsClearFirst(false);
      fetchSessions(selectedTerm.id);
    } catch (error) {
      console.error("Error copying sessions between terms:", error);
      toast.error(error.response?.data?.detail || "Failed to copy sessions");
    } finally {
      setCopySessionsFromLoading(false);
    }
  };

  // Derived: split terms into active vs archived and pre-compute the "next-term
  // carry-over" pointer used to visualise pro-rate commit-to-next-term flows.
  // For each finalized+unhidden term, next term = the earliest finalized+unhidden
  // term whose start_date > this term's end_date. Mirrors the backend logic in
  // `get_session_booking_options`.
  const activeTerms = (terms || []).filter(t => !isTermArchived(t));
  const archivedTerms = (terms || []).filter(t => isTermArchived(t));
  const nextTermByTermId = (() => {
    const map = {};
    const eligible = (terms || [])
      .filter(t => t?.is_finalized && !t?.is_hidden && t?.start_date)
      .sort((a, b) => (a.start_date < b.start_date ? -1 : 1));
    (terms || []).forEach(t => {
      if (!t?.end_date) return;
      const next = eligible.find(other => other.id !== t.id && other.start_date > t.end_date);
      if (next) map[t.id] = next;
    });
    return map;
  })();

  return (
    <div className="min-h-screen bg-[#0a0f1a] p-4 md:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
            <Link to="/academy">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </Link>
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-white">Term & Session Management</h1>
          </div>
          <div className="flex gap-2 w-full sm:w-auto">
            <Button onClick={openPriceAudit} variant="outline" className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10 w-full sm:w-auto" data-testid="price-audit-btn">
              <AlertCircle className="w-4 h-4 mr-2" />
              Price Audit
            </Button>
            <Button onClick={openCreateTermDialog} className="bg-purple-600 hover:bg-purple-700 w-full sm:w-auto">
              <Plus className="w-4 h-4 mr-2" />
              Create Term
            </Button>
          </div>
        </div>

        {/* Info Card */}
        <Card className="mb-6 p-4 bg-blue-500/10 border-blue-500/30">
          <p className="text-sm text-blue-300">
            <strong>Workflow:</strong> 1) Create a rate table → 2) Create a term and link to rate table → 3) Add sessions to term → 4) Finalize term to open enrollment
          </p>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Terms List */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Terms</h2>
              <Button onClick={openCreateTermDialog} className="bg-purple-600 hover:bg-purple-700 lg:hidden" size="sm">
                <Plus className="w-4 h-4 mr-1" />
                New Term
              </Button>
            </div>
            {loading ? (
              <p className="text-slate-400">Loading...</p>
            ) : terms.length === 0 ? (
              <Card className="p-6 text-center">
                <p className="text-slate-400 mb-4">No terms yet</p>
                <Button onClick={openCreateTermDialog} className="bg-purple-600 hover:bg-purple-700">
                  Create First Term
                </Button>
              </Card>
            ) : (
              <div className="space-y-3">
                {(() => {
                  const renderTermCard = (term) => (
                  <Card 
                    key={term?.id} 
                    className={`p-4 cursor-pointer border-2 ${
                      selectedTerm?.id === term?.id ? 'border-purple-500 bg-purple-500/10' : 'border-white/5'
                    }`}
                    onClick={() => {
                      setSelectedTerm(term);
                      setSessions([]); // Clear sessions immediately
                      fetchSessions(term?.id);
                    }}
                    data-testid={`term-card-${term?.id}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-bold text-lg">{term?.name}</h3>
                          {term?.programme_type && PROGRAMME_TYPE_PILLS[term.programme_type] && (() => {
                            const preset = PROGRAMME_TYPE_PILLS[term.programme_type];
                            const Ico = preset.Icon;
                            return (
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide border ${preset.cls}`}
                                data-testid={`term-pill-admin-${term.programme_type}`}
                                title={`Programme Type: ${preset.label}`}
                              >
                                <Ico className="w-3 h-3" />
                                {preset.label}
                              </span>
                            );
                          })()}
                          {term?.is_finalized && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-500/20 text-emerald-400">
                              <Lock className="w-3 h-3 mr-1" />
                              Finalized
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-400 mt-1">
                          {term?.start_date} | {term?.duration_value || term?.duration_weeks} {term?.duration_unit || 'weeks'}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          Rate: {getRateTableName(term?.rate_table_id)}
                        </p>
                        {/* Pro-rate carry-over pointer to next term */}
                        {nextTermByTermId[term?.id] && (
                          <p
                            className="text-[11px] text-cyan-300 mt-1.5 flex items-center gap-1"
                            data-testid={`term-next-link-${term.id}`}
                            title="Parents late-signing-up to this term will be offered a commit-to-next-term option"
                          >
                            <Link2 className="w-3 h-3" />
                            Pro-rate carry-over: <span className="font-medium">{nextTermByTermId[term.id].name}</span>
                          </p>
                        )}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {!term?.is_finalized && (
                          <>
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                openEditTermDialog(term);
                              }}
                              variant="outline"
                              size="sm"
                            >
                              <Edit className="w-4 h-4" />
                              <span className="ml-1 hidden sm:inline">Edit</span>
                            </Button>
                            <Button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFinalizeTerm(term.id);
                              }}
                              variant="outline"
                              size="sm"
                              className="text-emerald-400 hover:bg-emerald-500/10"
                            >
                              <CheckCircle className="w-4 h-4" />
                              <span className="ml-1 hidden sm:inline">Finalize</span>
                            </Button>
                          </>
                        )}
                        {/* View/Edit button for finalized terms */}
                        {term?.is_finalized && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditTermDialog(term);
                            }}
                            variant="outline"
                            size="sm"
                            className="text-blue-400 hover:bg-blue-500/10"
                            title="View or edit term details"
                          >
                            <Edit className="w-4 h-4" />
                            <span className="ml-1 hidden sm:inline">Edit</span>
                          </Button>
                        )}
                        {/* Hide/Show button for finalized terms */}
                        {term?.is_finalized && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleTermVisibility(term.id, term.is_hidden);
                            }}
                            variant="outline"
                            size="sm"
                            className={term.is_hidden 
                              ? "text-emerald-400 hover:bg-emerald-500/10" 
                              : "text-amber-400 hover:bg-amber-500/10"
                            }
                            title={term.is_hidden ? "Show term for new registrations" : "Hide term from new registrations"}
                          >
                            {term.is_hidden ? (
                              <>
                                <Eye className="w-4 h-4" />
                                <span className="ml-1 hidden sm:inline">Show</span>
                              </>
                            ) : (
                              <>
                                <EyeOff className="w-4 h-4" />
                                <span className="ml-1 hidden sm:inline">Hide</span>
                              </>
                            )}
                          </Button>
                        )}
                        {/* Re-sync / regenerate sessions button for finalized terms */}
                        {term?.is_finalized && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRegenerateSessions(term.id);
                            }}
                            variant="outline"
                            size="sm"
                            className="text-sky-400 hover:bg-sky-500/10"
                            title="Re-sync sessions: create any missing training sessions and refresh rosters from current enrolments"
                            disabled={resyncingTerm === term.id}
                            data-testid={`resync-sessions-${term.id}`}
                          >
                            <RefreshCw className={`w-4 h-4 ${resyncingTerm === term.id ? "animate-spin" : ""}`} />
                            <span className="ml-1 hidden sm:inline">
                              {resyncingTerm === term.id ? "Syncing..." : "Re-sync sessions"}
                            </span>
                          </Button>
                        )}
                        {/* Fix this term: dry-run preview → apply (repair stale links + regenerate/sync) */}
                        {term?.is_finalized && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              openFixTerm(term.id, term.name);
                            }}
                            variant="outline"
                            size="sm"
                            className="text-amber-400 hover:bg-amber-500/10"
                            title="Fix this term: preview & remap players' stale session links, stamp session teams, and re-sync rosters"
                            disabled={fixTermLoading === term.id}
                            data-testid={`fix-term-${term.id}`}
                          >
                            <Wrench className={`w-4 h-4 ${fixTermLoading === term.id ? "animate-pulse" : ""}`} />
                            <span className="ml-1 hidden sm:inline">
                              {fixTermLoading === term.id ? "Checking..." : "Fix this term"}
                            </span>
                          </Button>
                        )}
                        {/* Notify Parents button for finalized terms */}
                        {term?.is_finalized && !term?.is_hidden && (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleNotifyEnrollmentOpen(term.id, term.name);
                            }}
                            variant="outline"
                            size="sm"
                            className="text-purple-400 hover:bg-purple-500/10"
                            title="Notify all parents via Telegram that enrollment is open"
                            disabled={notifyingTerm === term.id}
                          >
                            <Send className="w-4 h-4" />
                            <span className="ml-1 hidden sm:inline">
                              {notifyingTerm === term.id ? "Sending..." : "Notify"}
                            </span>
                          </Button>
                        )}
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            openCopyTermDialog(term);
                          }}
                          variant="outline"
                          size="sm"
                          className="text-cyan-400 hover:bg-cyan-500/10"
                          title="Copy this term (sessions + settings) into a new draft"
                          data-testid={`copy-term-${term.id}`}
                        >
                          <Copy className="w-4 h-4" />
                          <span className="ml-1 hidden sm:inline">Copy</span>
                        </Button>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteTerm(term.id);
                          }}
                          variant="outline"
                          size="sm"
                          className="text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    {/* Hidden badge */}
                    {term?.is_hidden && (
                      <div className="mt-2 pt-2 border-t border-slate-700">
                        <span className="text-xs text-amber-400 bg-amber-500/10 px-2 py-1 rounded">
                          🚫 Hidden from new registrations
                        </span>
                      </div>
                    )}
                  </Card>
                  );  /* end renderTermCard */

                  return (
                    <>
                      {activeTerms.map(renderTermCard)}
                      {archivedTerms.length > 0 && (
                        <div className="mt-4 border-t border-slate-800 pt-3">
                          <button
                            type="button"
                            onClick={() => setShowArchived(!showArchived)}
                            className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-slate-800/40 hover:bg-slate-800/70 border border-slate-700 text-slate-300 text-sm font-medium"
                            data-testid="term-archive-toggle"
                          >
                            <span className="flex items-center gap-2">
                              <Archive className="w-4 h-4" />
                              Archived Terms ({archivedTerms.length})
                            </span>
                            {showArchived ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </button>
                          {showArchived && (
                            <div className="space-y-3 mt-3 opacity-80" data-testid="term-archive-list">
                              {archivedTerms.map(renderTermCard)}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>

          {/* Sessions List */}
          <div>
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
              <h2 className="text-xl font-bold">
                Sessions {selectedTerm ? `(${selectedTerm.name})` : ''}
              </h2>
              {selectedTerm && (
                <Button onClick={openCreateSessionDialog} className="bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto" size="sm">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Session
                </Button>
              )}
            </div>

            {/* Bulk import — copy sessions from another term */}
            {selectedTerm && (terms || []).length > 1 && (
              <Card
                className="mb-4 p-3 bg-slate-800/40 border-slate-700"
                data-testid="copy-sessions-from-panel"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Copy className="w-4 h-4 text-cyan-400" />
                  <p className="text-sm font-medium text-slate-200">Bulk-copy sessions from another term</p>
                </div>
                <p className="text-[11px] text-slate-400 mb-3">
                  Clones every session template (day, time, location, price, age-group) from the source term into <span className="text-white font-medium">{selectedTerm.name}</span>. Great after a Copy Term where you want to pull in more sessions.
                </p>
                <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
                  <select
                    value={copySessionsFromId}
                    onChange={(e) => setCopySessionsFromId(e.target.value)}
                    className="flex-1 px-3 py-2 bg-slate-900 border border-slate-700 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500"
                    disabled={copySessionsFromLoading}
                    data-testid="copy-sessions-from-select"
                  >
                    <option value="">— Pick a source term —</option>
                    {(terms || [])
                      .filter(t => t.id !== selectedTerm.id)
                      .map(t => (
                        <option key={t.id} value={t.id}>
                          {t.name} {t.is_finalized ? "· Finalized" : "· Draft"}{t.programme_type ? ` · ${t.programme_type}` : ""}
                        </option>
                      ))}
                  </select>
                  <Button
                    onClick={handleCopySessionsFromTerm}
                    disabled={!copySessionsFromId || copySessionsFromLoading}
                    className="bg-cyan-600 hover:bg-cyan-700 whitespace-nowrap"
                    size="sm"
                    data-testid="copy-sessions-from-submit"
                  >
                    {copySessionsFromLoading ? "Copying..." : "Copy Sessions"}
                  </Button>
                </div>
                <label className="mt-2 flex items-center gap-2 text-xs text-slate-400 cursor-pointer select-none">
                  <Checkbox
                    checked={copySessionsClearFirst}
                    onCheckedChange={(checked) => setCopySessionsClearFirst(!!checked)}
                    disabled={copySessionsFromLoading}
                    data-testid="copy-sessions-clear-first"
                  />
                  Clear existing sessions on <span className="text-white">{selectedTerm.name}</span> before copying (destructive)
                </label>
              </Card>
            )}

            {!selectedTerm ? (
              <Card className="p-6 text-center">
                <AlertCircle className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <p className="text-slate-400">Select a term to view its sessions</p>
              </Card>
            ) : sessions.length === 0 ? (
              <Card className="p-6 text-center">
                <p className="text-slate-400 mb-4">No sessions yet</p>
                <Button onClick={openCreateSessionDialog} className="bg-emerald-600 hover:bg-emerald-700">
                  Add First Session
                </Button>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Draft Sessions Alert Banner */}
                {(() => {
                  const draftSessions = sessions.filter(s => s.is_finalized === false);
                  if (draftSessions.length === 0) return null;
                  
                  return (
                    <Card className="p-4 bg-amber-500/10 border-amber-500/30 border-2">
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
                            <AlertCircle className="w-5 h-5 text-amber-400" />
                          </div>
                          <div>
                            <h4 className="font-bold text-amber-400">
                              {draftSessions.length} Unfinalised Session{draftSessions.length !== 1 ? 's' : ''}
                            </h4>
                            <p className="text-sm text-slate-400">
                              Draft sessions are shown at the top of each day. Finalize them to make available for registration.
                            </p>
                          </div>
                        </div>
                        <Button
                          onClick={async () => {
                            if (!window.confirm(`Finalize all ${draftSessions.length} draft session(s)?`)) return;
                            for (const s of draftSessions) {
                              await handleFinalizeSession(s.id, true);
                            }
                            toast.success(`Finalized ${draftSessions.length} session(s)`);
                          }}
                          className="bg-amber-600 hover:bg-amber-700 text-white"
                          size="sm"
                        >
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Finalize All
                        </Button>
                      </div>
                    </Card>
                  );
                })()}
                
                {/* Sort sessions: Draft first → Day of Week → Age Group → Gender → Time */}
                {(() => {
                  const sortedSessions = [...sessions].sort((a, b) => {
                    // 0. Draft sessions come FIRST within each day
                    const draftA = a.is_finalized === false ? 0 : 1;
                    const draftB = b.is_finalized === false ? 0 : 1;
                    
                    // 1. First sort by day of week (Monday=1 to Sunday=7)
                    const dayA = a.day_of_week || 8;
                    const dayB = b.day_of_week || 8;
                    if (dayA !== dayB) return dayA - dayB;
                    
                    // 2. Then by draft status (drafts first)
                    if (draftA !== draftB) return draftA - draftB;
                    
                    // 3. Then by age group name
                    const ageA = a.age_group_name || 'zzz';
                    const ageB = b.age_group_name || 'zzz';
                    if (ageA !== ageB) return ageA.localeCompare(ageB);
                    
                    // 4. Then by gender (Boys first, then Girls, then Mixed)
                    const genderOrder = { male: 0, female: 1, mixed: 2 };
                    const genderA = genderOrder[a.gender] ?? 2;
                    const genderB = genderOrder[b.gender] ?? 2;
                    if (genderA !== genderB) return genderA - genderB;
                    
                    // 5. Finally by start time
                    const timeA = a.start_time || '23:59';
                    const timeB = b.start_time || '23:59';
                    return timeA.localeCompare(timeB);
                  });

                  // Group sessions by day for visual organization
                  const groupedByDay = sortedSessions.reduce((acc, session) => {
                    const day = session.day_of_week || 0;
                    if (!acc[day]) acc[day] = [];
                    acc[day].push(session);
                    return acc;
                  }, {});

                  return Object.entries(groupedByDay).map(([dayValue, daySessions]) => {
                    const dayLabel = DAYS_OF_WEEK.find(d => d.value === parseInt(dayValue))?.label || 'Unknown';
                    const draftCountForDay = daySessions.filter(s => s.is_finalized === false).length;
                    
                    return (
                      <div key={dayValue} className="space-y-2">
                        {/* Day Header */}
                        <div className="flex items-center gap-2 sticky top-0 bg-[#0a0f1a] py-2 z-10">
                          <div className="h-px flex-1 bg-slate-700"></div>
                          <span className="text-sm font-bold text-yellow-500 px-3 py-1 bg-yellow-500/10 rounded-full">
                            {dayLabel}
                          </span>
                          <span className="text-xs text-slate-400">
                            ({daySessions.length} session{daySessions.length !== 1 ? 's' : ''})
                          </span>
                          {draftCountForDay > 0 && (
                            <span className="text-xs font-bold text-amber-400 bg-amber-500/20 px-2 py-0.5 rounded-full animate-pulse">
                              ⏳ {draftCountForDay} draft{draftCountForDay !== 1 ? 's' : ''}
                            </span>
                          )}
                          <div className="h-px flex-1 bg-slate-700"></div>
                        </div>
                        
                        {/* Sessions for this day - Grouped by time slot */}
                        <div className="space-y-3 pl-2 border-l-2 border-yellow-500/20">
                          {(() => {
                            // Group sessions by time slot (start_time + end_time + location)
                            const timeSlotGroups = daySessions.reduce((acc, session) => {
                              const key = `${session.start_time}-${session.end_time}-${session.location || 'Main Field'}`;
                              if (!acc[key]) {
                                acc[key] = {
                                  start_time: session.start_time,
                                  end_time: session.end_time,
                                  location: session.location || 'Main Field',
                                  sessions: []
                                };
                              }
                              acc[key].sessions.push(session);
                              return acc;
                            }, {});

                            return Object.values(timeSlotGroups).map((group, groupIdx) => {
                              // Sort sessions within this group: drafts first
                              const sortedGroupSessions = [...group.sessions].sort((a, b) => {
                                const draftA = a.is_finalized === false ? 0 : 1;
                                const draftB = b.is_finalized === false ? 0 : 1;
                                return draftA - draftB;
                              });
                              const draftCountInGroup = sortedGroupSessions.filter(s => s.is_finalized === false).length;
                              
                              // Check if all sessions in this time slot have the same type
                              const sessionTypes = [...new Set(sortedGroupSessions.map(s => s.session_type || 'training'))];
                              const hasUniformType = sessionTypes.length === 1;
                              const sessionTypeConfig = {
                                social: { label: '🎯 Social', bg: 'bg-green-500/20 text-green-400 border-green-500/30' },
                                competitive: { label: '⚽ Competitive', bg: 'bg-orange-500/20 text-orange-400 border-orange-500/30' },
                                training: { label: '🏃 Training', bg: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30' },
                                game: { label: '🏆 Game', bg: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30' },
                                tournament: { label: '🥇 Tournament', bg: 'bg-red-500/20 text-red-400 border-red-500/30' }
                              };
                              const typeInfo = hasUniformType ? sessionTypeConfig[sessionTypes[0]] : null;
                              
                              return (
                              <Card key={groupIdx} className={`p-4 bg-slate-800/50 ${draftCountInGroup > 0 ? 'border-2 border-amber-500/50' : ''} ${typeInfo ? `border-l-4 ${typeInfo.bg.split(' ')[2]}` : ''}`}>
                                {/* Time Slot Header */}
                                <div className="flex items-center justify-between mb-3">
                                  <div>
                                    <div className="flex items-center gap-2 mb-1">
                                      <h3 className="text-lg font-bold text-white">
                                        {group.start_time} - {group.end_time}
                                      </h3>
                                      {typeInfo && (
                                        <span className={`text-xs px-2 py-0.5 rounded font-medium ${typeInfo.bg.split(' ').slice(0,2).join(' ')}`}>
                                          {typeInfo.label}
                                        </span>
                                      )}
                                      {draftCountInGroup > 0 && (
                                        <span className="text-xs px-2 py-0.5 rounded font-medium bg-amber-500/20 text-amber-400">
                                          ⏳ {draftCountInGroup} draft{draftCountInGroup !== 1 ? 's' : ''}
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-sm text-slate-400">{group.location}</p>
                                  </div>
                                  <div className="text-right">
                                    <span className="text-xs bg-slate-700 text-slate-300 px-2 py-1 rounded">
                                      {sortedGroupSessions.length} session{sortedGroupSessions.length > 1 ? 's' : ''}
                                    </span>
                                  </div>
                                </div>

                                {/* Age Groups Grid */}
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                  {sortedGroupSessions.map((session) => {
                                    const genderLabel = session?.gender === 'male' ? '👦 Boys' : session?.gender === 'female' ? '👧 Girls' : '👫 Mixed';
                                    const genderBg = session?.gender === 'male' ? 'bg-blue-500/10 border-blue-500/30' : 
                                                     session?.gender === 'female' ? 'bg-pink-500/10 border-pink-500/30' : 
                                                     'bg-purple-500/10 border-purple-500/30';
                                    
                                    // Session type styling
                                    const sessionTypeConfig = {
                                      social: { label: '🎯 Social', bg: 'bg-green-500/20 text-green-400' },
                                      competitive: { label: '⚽ Competitive', bg: 'bg-orange-500/20 text-orange-400' },
                                      training: { label: '🏃 Training', bg: 'bg-cyan-500/20 text-cyan-400' },
                                      game: { label: '🏆 Game', bg: 'bg-yellow-500/20 text-yellow-400' },
                                      tournament: { label: '🥇 Tournament', bg: 'bg-red-500/20 text-red-400' }
                                    };
                                    const typeInfo = sessionTypeConfig[session?.session_type] || sessionTypeConfig.training;
                                    
                                    return (
                                      <div 
                                        key={session.id} 
                                        className={`p-3 rounded-lg border ${genderBg} relative group`}
                                      >
                                        <div className="flex items-start justify-between">
                                          <div className="flex-1">
                                            {/* Draft Badge - Show if session not finalized */}
                                            {session.is_finalized === false && (
                                              <span className="inline-block text-xs px-2 py-0.5 rounded font-medium mb-2 bg-amber-500/20 text-amber-400 border border-amber-500/30">
                                                ⏳ DRAFT
                                              </span>
                                            )}
                                            
                                            {/* Session Type Badge - TOP */}
                                            <span className={`inline-block text-xs px-2 py-0.5 rounded font-medium mb-2 ml-1 ${typeInfo.bg}`}>
                                              {typeInfo.label}
                                            </span>
                                            
                                            {/* Age Group Name - Prominent */}
                                            <div className="flex items-center gap-2 mb-1">
                                              {session.age_group_name ? (
                                                <span className="text-lg font-bold text-white">
                                                  {session.age_group_name}
                                                </span>
                                              ) : (
                                                <span className="text-sm text-slate-400">No age group</span>
                                              )}
                                            </div>
                                            
                                            {/* Age Range */}
                                            {(session.min_age || session.max_age) && (
                                              <p className="text-xs text-slate-400">
                                                Ages {session.min_age || '?'} - {session.max_age || '?'}
                                              </p>
                                            )}
                                            
                                            {/* Gender Badge */}
                                            <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded ${
                                              session?.gender === 'male' ? 'bg-blue-500/20 text-blue-400' : 
                                              session?.gender === 'female' ? 'bg-pink-500/20 text-pink-400' : 
                                              'bg-purple-500/20 text-purple-400'
                                            }`}>
                                              {genderLabel}
                                            </span>
                                            
                                            {/* Price — reflects the rate table when one drives pricing */}
                                            <p className="text-xs text-emerald-400 mt-1 font-medium">
                                              {sessionPriceLabel(session)}
                                            </p>
                                            {isProgressive && (
                                              <span className="inline-block mt-0.5 text-[10px] text-amber-300/90 bg-amber-500/10 border border-amber-500/30 rounded px-1.5 py-0.5" title="Progressive pricing — this per-session price is what actually bills">
                                                Billing price (manual)
                                              </span>
                                            )}
                                            
                                            {/* Enrollment Count */}
                                            {session.enrollment_count !== undefined && (
                                              <p className={`text-xs mt-1 font-medium ${session.enrollment_count > 0 ? 'text-blue-400' : 'text-slate-500'}`}>
                                                👤 {session.enrollment_count} enrolled
                                              </p>
                                            )}
                                          </div>
                                          
                                          {/* Action Buttons */}
                                          <div className="flex flex-col gap-1">
                                            {/* Finalize Button - Only for draft sessions */}
                                            {session.is_finalized === false && (
                                              <Button
                                                onClick={() => handleFinalizeSession(session.id)}
                                                variant="ghost"
                                                size="icon"
                                                className="h-6 w-6 text-green-400 hover:bg-green-500/20"
                                                title="Finalize Session"
                                              >
                                                <CheckCircle className="w-3 h-3" />
                                              </Button>
                                            )}
                                            <Button
                                              onClick={() => openCopySessionDialog(session)}
                                              variant="ghost"
                                              size="icon"
                                              className="h-6 w-6 text-blue-400 hover:bg-blue-500/20"
                                              title="Copy"
                                            >
                                              <Copy className="w-3 h-3" />
                                            </Button>
                                            <Button
                                              onClick={() => openEditSessionDialog(session)}
                                              variant="ghost"
                                              size="icon"
                                              className="h-6 w-6 text-slate-400 hover:bg-slate-500/20"
                                              title="Edit"
                                            >
                                              <Edit className="w-3 h-3" />
                                            </Button>
                                            {/* Delete - Only if no enrollments */}
                                            <Button
                                              onClick={() => handleDeleteSession(session.id, session.enrollment_count)}
                                              variant="ghost"
                                              size="icon"
                                              className={`h-6 w-6 ${session.enrollment_count > 0 ? 'text-slate-600 cursor-not-allowed' : 'text-red-400 hover:bg-red-500/20'}`}
                                              title={session.enrollment_count > 0 ? `Cannot delete - ${session.enrollment_count} enrolled` : "Delete"}
                                              disabled={session.enrollment_count > 0}
                                            >
                                              <Trash2 className="w-3 h-3" />
                                            </Button>
                                          </div>
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>

                                {/* Quick Summary */}
                                <div className="mt-3 pt-2 border-t border-slate-700 flex flex-wrap gap-1">
                                  <span className="text-xs text-slate-400">Age groups:</span>
                                  {sortedGroupSessions.map((s, i) => (
                                    <span key={i} className="text-xs bg-purple-500/20 text-purple-400 px-1.5 py-0.5 rounded">
                                      {s.age_group_name || 'All ages'}
                                    </span>
                                  ))}
                                </div>
                              </Card>
                              );
                            });
                          })()}
                        </div>
                      </div>
                    );
                  });
                })()}
              </div>
            )}
          </div>
        </div>

        {/* Copy Term Dialog */}
        <Dialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Copy className="w-5 h-5 text-cyan-400" />
                Copy Term
              </DialogTitle>
            </DialogHeader>
            {sourceTerm && (
              <div className="space-y-4 py-2">
                <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                  <p className="text-xs text-cyan-300">
                    Copying from <strong>{sourceTerm.name}</strong>
                    {sourceTerm.programme_type && (
                      <span className="ml-2 uppercase tracking-wide">· {sourceTerm.programme_type}</span>
                    )}
                  </p>
                  <p className="text-[10px] text-slate-400 mt-1">
                    New term will start as <strong>draft</strong> and <strong>hidden from parents</strong>. Edit, finalise and unhide when ready.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">New Term Name *</label>
                  <Input
                    value={copyNewName}
                    onChange={(e) => setCopyNewName(e.target.value)}
                    placeholder="e.g., 2026 Term 3"
                    data-testid="copy-term-name-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">New Start Date *</label>
                  <Input
                    type="date"
                    value={copyNewStartDate}
                    onChange={(e) => setCopyNewStartDate(e.target.value)}
                    data-testid="copy-term-start-input"
                  />
                  <p className="text-[11px] text-slate-500 mt-1">
                    Defaults to the day after the source term ends. End date is auto-calculated from the source term&apos;s duration + breaks.
                  </p>
                </div>

                <label className="flex items-center gap-2 cursor-pointer p-3 rounded-lg border border-slate-700 hover:bg-slate-800/40">
                  <Checkbox
                    checked={copySessions}
                    onCheckedChange={(checked) => setCopySessions(!!checked)}
                    data-testid="copy-sessions-toggle"
                  />
                  <div>
                    <p className="text-sm font-medium">Also copy session templates</p>
                    <p className="text-[11px] text-slate-400">
                      Duplicates every day/time/location/price session on the source term.
                    </p>
                  </div>
                </label>

                <div className="text-[11px] text-slate-400 p-3 bg-slate-900/40 rounded border border-slate-800">
                  <p className="font-medium text-slate-300 mb-1">What gets copied</p>
                  <p>✓ Duration &amp; breaks · Programme Type · Description &amp; Highlights · Term Breaks{copySessions ? " · All session templates (incl. their $/session prices)" : ""}</p>
                  <p className="mt-1 text-slate-500">✗ Finalised status · Registrations · Payments</p>
                  <p className="mt-2 text-amber-400/90">⚠ Rate table is <span className="font-semibold">not</span> copied — assign a fresh rate table on the new term (it won&apos;t finalise without one). This protects your rates from carrying over old figures.</p>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => setShowCopyDialog(false)}
                disabled={copyLoading}
                data-testid="copy-term-cancel"
              >
                Cancel
              </Button>
              <Button
                onClick={handleCopyTermSubmit}
                className="bg-cyan-600 hover:bg-cyan-700"
                disabled={copyLoading}
                data-testid="copy-term-submit"
              >
                {copyLoading ? "Copying..." : "Create Copy"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Term Dialog */}
        <Dialog open={showTermDialog} onOpenChange={setShowTermDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {viewOnlyMode ? "View Term Details" : (editingTerm ? "Edit Term" : "Create Term")}
                {viewOnlyMode && (
                  <Badge className="ml-2 bg-blue-500/20 text-blue-400">Read Only</Badge>
                )}
              </DialogTitle>
            </DialogHeader>
            
            {/* Banner for finalized terms */}
            {editingTerm?.is_finalized && !viewOnlyMode && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 mb-4">
                <p className="text-sm text-emerald-400 flex items-center gap-2">
                  This term is finalized but has no paid enrollments — all fields are editable.
                </p>
              </div>
            )}
            {viewOnlyMode && (
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-3 mb-4">
                <p className="text-sm text-blue-400 flex items-center gap-2">
                  <Lock className="w-4 h-4" />
                  This term is finalized. Most fields are read-only.
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  💡 You can still change the <strong>Rate Table</strong> to fix pricing for new registrations.
                </p>
              </div>
            )}
            
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium mb-2">Term Name *</label>
                <Input
                  value={termName}
                  onChange={(e) => setTermName(e.target.value)}
                  placeholder="e.g., Term 1 2026, Holiday Camp Dec 2026"
                  disabled={viewOnlyMode}
                  className={viewOnlyMode ? "bg-slate-800/50 cursor-not-allowed" : ""}
                  data-testid="term-name-input"
                />
              </div>

              {/* Programme Type Presentation Group */}
              <div className="rounded-lg border border-slate-700 bg-slate-900/40 p-4 space-y-4">
                <div>
                  <h4 className="text-sm font-semibold text-slate-200">Programme Type & Description</h4>
                  <p className="text-xs text-slate-400 mt-1">
                    Shown to parents when they choose a term — helps them pick faster.
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Programme Type</label>
                  <select
                    value={termProgrammeType}
                    onChange={(e) => setTermProgrammeType(e.target.value)}
                    disabled={viewOnlyMode}
                    className={`w-full border border-slate-700 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${viewOnlyMode ? "bg-slate-800/50 cursor-not-allowed" : "bg-slate-900"}`}
                    data-testid="term-programme-type-select"
                  >
                    <option value="">— None / Uncategorised —</option>
                    <option value="competitive">🏆 Competitive — game-day selection, all-access</option>
                    <option value="development">📈 Development — skill focus, no games</option>
                    <option value="recreational">😊 Recreational — social, low-pressure</option>
                    <option value="standard">📅 Standard Term — regular full-term training</option>
                    <option value="adhoc">⚡ Ad-Hoc — pay per session, flexible</option>
                    <option value="girls">👥 Girls — girls-only pricing / sessions</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Short Description (1–2 sentences)</label>
                  <textarea
                    value={termDescription}
                    onChange={(e) => setTermDescription(e.target.value)}
                    placeholder="e.g., One flat price. All training sessions included. Players are up for game-day selection."
                    disabled={viewOnlyMode}
                    rows={2}
                    maxLength={220}
                    className={`w-full border border-slate-700 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${viewOnlyMode ? "bg-slate-800/50 cursor-not-allowed" : "bg-slate-900"}`}
                    data-testid="term-description-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">{termDescription.length}/220</p>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Best For (one line)</label>
                  <Input
                    value={termTargetAudience}
                    onChange={(e) => setTermTargetAudience(e.target.value)}
                    placeholder="e.g., Players ready to compete on match-days"
                    disabled={viewOnlyMode}
                    maxLength={120}
                    className={viewOnlyMode ? "bg-slate-800/50 cursor-not-allowed" : ""}
                    data-testid="term-target-audience-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Highlights (up to 3 bullets)</label>
                  <div className="space-y-2">
                    {termHighlights.map((h, idx) => (
                      <Input
                        key={idx}
                        value={h}
                        onChange={(e) => {
                          const next = [...termHighlights];
                          next[idx] = e.target.value;
                          setTermHighlights(next);
                        }}
                        placeholder={
                          idx === 0
                            ? "e.g., All training days included"
                            : idx === 1
                            ? "e.g., Priority game-day selection"
                            : "e.g., Best value per session"
                        }
                        disabled={viewOnlyMode}
                        maxLength={80}
                        className={viewOnlyMode ? "bg-slate-800/50 cursor-not-allowed" : ""}
                        data-testid={`term-highlight-${idx}-input`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    Empty bullets are ignored. Keep each under 80 characters.
                  </p>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Start Date *</label>
                <Input
                  type="date"
                  value={termStartDate}
                  onChange={(e) => setTermStartDate(e.target.value)}
                  disabled={viewOnlyMode}
                  className={viewOnlyMode ? "bg-slate-800/50 cursor-not-allowed" : ""}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Duration *</label>
                  <Input
                    type="number"
                    value={termDurationValue}
                    onChange={(e) => setTermDurationValue(e.target.value)}
                    min="1"
                    disabled={viewOnlyMode}
                    className={viewOnlyMode ? "bg-slate-800/50 cursor-not-allowed" : ""}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Unit *</label>
                  <Select value={termDurationUnit} onValueChange={setTermDurationUnit} disabled={viewOnlyMode}>
                    <SelectTrigger className={viewOnlyMode ? "bg-slate-800/50 cursor-not-allowed" : ""}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="days">Days</SelectItem>
                      <SelectItem value="weeks">Weeks</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Rate Table (required before finalizing)</label>
                <select
                  value={termRateTableId}
                  onChange={(e) => setTermRateTableId(e.target.value)}
                  disabled={viewOnlyMode && !editingTerm?.is_finalized}
                  className={`w-full border border-slate-700 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-purple-500 ${viewOnlyMode && !editingTerm?.is_finalized ? "bg-slate-800/50 cursor-not-allowed" : ""}`}
                >
                  <option value="none">None (assign later)</option>
                  {rateTables?.map((table) => (
                    <option key={table?.id} value={table?.id}>
                      {table?.name}
                    </option>
                  ))}
                </select>
                {!viewOnlyMode && (
                  <p className="text-xs text-slate-400 mt-1">Rate table must be assigned before finalizing the term</p>
                )}
                {viewOnlyMode && editingTerm?.is_finalized && (
                  <p className="text-xs text-amber-400 mt-1">⚠️ Changing the rate table only affects NEW registrations. Existing payments are not affected.</p>
                )}
              </div>

              {/* Term Breaks Section */}
              <div className="border-t pt-4">
                <label className="block text-sm font-medium mb-2">Term Breaks (Optional)</label>
                <p className="text-xs text-slate-400 mb-3">Add holiday weeks or breaks. End date will auto-extend to accommodate breaks.</p>
                
                {termBreaks.map((brk, index) => (
                  <div key={index} className="flex items-center gap-2 mb-2 p-3 bg-orange-500/10 rounded-lg border border-orange-500/30">
                    <div className="flex-1 grid grid-cols-3 gap-2">
                      <Input
                        type="date"
                        value={brk.start_date || ""}
                        onChange={(e) => {
                          const newBreaks = [...termBreaks];
                          newBreaks[index].start_date = e.target.value;
                          setTermBreaks(newBreaks);
                        }}
                        className="text-sm"
                        disabled={viewOnlyMode}
                      />
                      <Input
                        type="date"
                        value={brk.end_date || ""}
                        onChange={(e) => {
                          const newBreaks = [...termBreaks];
                          newBreaks[index].end_date = e.target.value;
                          setTermBreaks(newBreaks);
                        }}
                        className="text-sm"
                        disabled={viewOnlyMode}
                      />
                      <Input
                        type="text"
                        placeholder="Reason (e.g., CNY)"
                        value={brk.reason || ""}
                        onChange={(e) => {
                          const newBreaks = [...termBreaks];
                          newBreaks[index].reason = e.target.value;
                          setTermBreaks(newBreaks);
                        }}
                        className="text-sm"
                        disabled={viewOnlyMode}
                      />
                    </div>
                    {!viewOnlyMode && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newBreaks = termBreaks.filter((_, i) => i !== index);
                          setTermBreaks(newBreaks);
                        }}
                        className="text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                
                {!viewOnlyMode && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setTermBreaks([...termBreaks, { start_date: "", end_date: "", reason: "" }])}
                    className="mt-2"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Break
                  </Button>
                )}
              </div>

              {/* Cancel Single Session Dates (per-weekday) — existing terms only */}
              {editingTerm?.id && (
                <div className="border-t pt-4">
                  <label className="block text-sm font-medium mb-1">Cancel a single session date</label>
                  <p className="text-xs text-slate-400 mb-3">
                    Tap a date to cancel just that one session (e.g. a rained-out Saturday). Only players enrolled on that weekday are affected — other days keep running and billing normally. This does <strong>not</strong> change the term length.
                  </p>
                  {loadingSessionDates ? (
                    <p className="text-sm text-slate-400">Loading session dates…</p>
                  ) : !sessionDatesData || (sessionDatesData.weekdays || []).length === 0 ? (
                    <p className="text-sm text-slate-500">No session templates yet — add sessions to this term first.</p>
                  ) : (
                    <div className="space-y-3" data-testid="cancel-session-dates">
                      {sessionDatesData.weekdays.map((wd) => (
                        <div key={wd.day_of_week} className="p-3 bg-[#0a0f1a] rounded-lg border border-slate-700">
                          <p className="text-sm font-medium mb-2">{wd.day_name} <span className="text-xs text-slate-400">{wd.start_time}{wd.end_time ? `–${wd.end_time}` : ""}</span></p>
                          <div className="flex flex-wrap gap-2">
                            {wd.dates.map((dt) => {
                              const label = new Date(dt.date + "T00:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" });
                              return (
                                <button
                                  key={dt.date}
                                  type="button"
                                  disabled={viewOnlyMode || cancelBusy}
                                  onClick={() => handleDateChipClick(dt)}
                                  data-testid={`cancel-date-${dt.date}`}
                                  title={dt.cancelled ? "Cancelled — tap to restore" : "Tap to cancel this session"}
                                  className={`text-xs px-2.5 py-1 rounded-md border transition-colors disabled:opacity-50 ${
                                    dt.cancelled
                                      ? "bg-red-500/20 border-red-500 text-red-300 line-through"
                                      : "bg-slate-800 border-slate-600 text-slate-300 hover:border-red-500/60"
                                  }`}
                                >
                                  {label}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                      <p className="text-xs text-slate-500">Red = cancelled. Tap a red date to restore it.</p>
                    </div>
                  )}
                </div>
              )}

              {/* End Date Section */}
              <div className="border-t pt-4">
                <label className="block text-sm font-medium mb-2">End Date</label>
                
                <div className="space-y-3">
                  <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-[#0a0f1a]">
                    <input
                      type="radio"
                      name="endDateMode"
                      value="auto"
                      checked={endDateMode === "auto"}
                      onChange={() => setEndDateMode("auto")}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-white">Auto-calculate (Recommended)</span>
                      {termStartDate && termDurationValue && (
                        <p className="text-sm text-emerald-400 mt-1">
                          📅 End Date: <strong>{calculatedEndDate}</strong>
                          {breakWeeksCount > 0 && (
                            <span className="text-slate-400"> ({termDurationValue} training weeks + {breakWeeksCount} break week{breakWeeksCount > 1 ? 's' : ''})</span>
                          )}
                        </p>
                      )}
                    </div>
                  </label>
                  
                  <label className="flex items-start gap-3 p-3 rounded-lg border cursor-pointer hover:bg-[#0a0f1a]">
                    <input
                      type="radio"
                      name="endDateMode"
                      value="manual"
                      checked={endDateMode === "manual"}
                      onChange={() => setEndDateMode("manual")}
                      className="mt-1"
                    />
                    <div className="flex-1">
                      <span className="font-medium text-white">Manual override</span>
                      {endDateMode === "manual" && (
                        <div className="mt-2">
                          <Input
                            type="date"
                            value={manualEndDate}
                            onChange={(e) => setManualEndDate(e.target.value)}
                            className="max-w-xs"
                          />
                          <p className="text-xs text-orange-400 mt-1">
                            ⚠️ Manual dates may not align with training weeks
                          </p>
                        </div>
                      )}
                    </div>
                  </label>
                </div>
              </div>

              {/* Summary */}
              {termStartDate && termDurationValue && (
                <div className="bg-blue-500/10 p-4 rounded-lg border border-blue-500/30">
                  <h4 className="font-medium text-blue-300 mb-2">📊 Summary</h4>
                  <ul className="text-sm text-blue-400 space-y-1">
                    <li>• Training weeks: <strong>{termDurationValue}</strong> (billable)</li>
                    <li>• Break weeks: <strong>{breakWeeksCount}</strong></li>
                    <li>• Calendar span: <strong>{parseInt(termDurationValue) + breakWeeksCount}</strong> weeks</li>
                    <li>• Sessions generated: <strong>{termDurationValue}</strong> per enrolled day</li>
                  </ul>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button onClick={() => setShowTermDialog(false)} variant="outline">
                  {viewOnlyMode ? "Close" : "Cancel"}
                </Button>
                {/* Allow changing rate table for finalized terms */}
                {viewOnlyMode && editingTerm?.is_finalized && termRateTableId !== editingTerm?.rate_table_id && (
                  <Button 
                    onClick={handleChangeRateTable} 
                    className="bg-amber-600 hover:bg-amber-700"
                    data-testid="save-rate-table-btn"
                  >
                    Save Rate Table Change
                  </Button>
                )}
                {!viewOnlyMode && (
                  <Button onClick={handleTermSubmit} className="bg-purple-600 hover:bg-purple-700">
                    {editingTerm ? "Update Term" : "Create Term"}
                  </Button>
                )}
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Session Dialog */}
        <Dialog open={showSessionDialog} onOpenChange={setShowSessionDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingSession ? "Edit Session" : "Create Session"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="block text-sm font-medium mb-2">Day of Week *</label>
                <Select value={sessionDay.toString()} onValueChange={(v) => setSessionDay(parseInt(v))}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DAYS_OF_WEEK.map((day) => (
                      <SelectItem key={day.value} value={day.value.toString()}>
                        {day.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Copy to Multiple Days - Only show when creating new session */}
              {!editingSession && (
                <div className="p-3 bg-blue-500/10 rounded-lg border border-blue-500/30">
                  <div className="flex items-center gap-2 mb-2">
                    <Checkbox
                      id="copy-multiple-days"
                      checked={copyToMultipleDays}
                      onCheckedChange={setCopyToMultipleDays}
                    />
                    <label htmlFor="copy-multiple-days" className="text-sm font-medium text-blue-400 cursor-pointer">
                      Also create for other days
                    </label>
                  </div>
                  
                  {copyToMultipleDays && (
                    <div className="mt-3">
                      <p className="text-xs text-blue-400 mb-2">Select additional days:</p>
                      <div className="flex flex-wrap gap-2">
                        {DAYS_OF_WEEK.filter(d => d.value !== parseInt(sessionDay)).map((day) => (
                          <label
                            key={day.value}
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full cursor-pointer text-sm transition-all ${
                              selectedDays.includes(day.value)
                                ? 'bg-blue-600 text-white'
                                : 'bg-slate-900/50 border border-blue-500/30 text-blue-400 hover:bg-blue-500/20'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={selectedDays.includes(day.value)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setSelectedDays([...selectedDays, day.value]);
                                } else {
                                  setSelectedDays(selectedDays.filter(d => d !== day.value));
                                }
                              }}
                              className="sr-only"
                            />
                            {day.label}
                          </label>
                        ))}
                      </div>
                      {selectedDays.length > 0 && (
                        <p className="text-xs text-blue-400 mt-2">
                          📋 Will create {1 + selectedDays.length} sessions ({DAYS_OF_WEEK.find(d => d.value === parseInt(sessionDay))?.label}
                          {selectedDays.map(d => `, ${DAYS_OF_WEEK.find(day => day.value === d)?.label}`).join('')})
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Start Time *</label>
                  <Input
                    type="time"
                    value={sessionStartTime}
                    onChange={(e) => setSessionStartTime(e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">End Time *</label>
                  <Input
                    type="time"
                    value={sessionEndTime}
                    onChange={(e) => setSessionEndTime(e.target.value)}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Session Type *</label>
                  <Select value={sessionType} onValueChange={setSessionType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="social">🎯 Social / Recreational</SelectItem>
                      <SelectItem value="competitive">⚽ Competitive</SelectItem>
                      <SelectItem value="training">🏃 Training</SelectItem>
                      <SelectItem value="game">🏆 Game Day</SelectItem>
                      <SelectItem value="tournament">🥇 Tournament</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Base Price ($){!isRateDriven && " *"}
                    {isRateDriven && (
                      <span className="text-xs text-blue-400 font-normal ml-2">
                        (From Rate Table)
                      </span>
                    )}
                    {isProgressive && (
                      <span className="text-xs text-amber-300 font-normal ml-2">
                        (Used for billing)
                      </span>
                    )}
                  </label>
                  <Input
                    type="number"
                    value={sessionPrice}
                    onChange={(e) => setSessionPrice(e.target.value)}
                    placeholder={isRateDriven ? "Set by rate table" : "e.g., 50"}
                    min="0"
                    step="0.01"
                    disabled={isRateDriven}
                    className={isRateDriven ? "bg-slate-800/50 cursor-not-allowed" : ""}
                    data-testid="session-base-price-input"
                  />
                  {isVolumePricing && (
                    <p className="text-xs text-blue-400 mt-1">
                      💡 Volume pricing active — price comes from the Rate Table: ${firstTierPrice}/session. No need to enter a price.
                    </p>
                  )}
                  {isMaxCapacity && mcPrice && (
                    <p className="text-xs text-blue-400 mt-1">
                      💡 Max-Capacity pricing active — ${mcPrice}/session · ${ (parseFloat(mcPrice) * mcMaxSessions).toFixed(2) }/week (×{mcMaxSessions}) from the Rate Table. No need to enter a price.
                    </p>
                  )}
              </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Location *</label>
                <Select value={sessionLocation} onValueChange={setSessionLocation}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select location" />
                  </SelectTrigger>
                  <SelectContent>
                    {locations.filter(loc => loc.is_active).map((loc) => (
                      <SelectItem key={loc.id} value={loc.name}>
                        {loc.name}
                      </SelectItem>
                    ))}
                    <SelectItem value="Other">Other (Custom)</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-400 mt-1">
                  Manage locations in the Academy Dashboard → Locations
                </p>
              </div>

              {sessionLocation === "Other" && (
                <div>
                  <label className="block text-sm font-medium mb-2">Custom Location *</label>
                  <Input
                    value={sessionCustomLocation}
                    onChange={(e) => setSessionCustomLocation(e.target.value)}
                    placeholder="Enter custom location name"
                  />
                </div>
              )}

              {/* Age Groups Section - NEW IMPROVED UI */}
              <div className="border-t pt-4 mt-4">
                <h4 className="font-medium text-white mb-3 flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Age Groups
                </h4>
                <p className="text-sm text-slate-400 mb-4">
                  Create sessions for multiple age groups at once. Each age group will create a separate session.
                </p>
                
                {/* Added Age Groups */}
                {sessionAgeGroups.length > 0 && (
                  <div className="space-y-2 mb-4">
                    <p className="text-xs text-slate-400 font-medium">Sessions to create:</p>
                    {sessionAgeGroups.map((ag) => (
                      <div key={ag.id} className="flex items-center justify-between p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-emerald-400">{ag.name}</span>
                          <span className="text-sm text-slate-400">Ages {ag.minAge}-{ag.maxAge}</span>
                          <Badge className={
                            ag.gender === "male" ? "bg-blue-500/20 text-blue-400" :
                            ag.gender === "female" ? "bg-pink-500/20 text-pink-400" :
                            "bg-purple-500/20 text-purple-400"
                          }>
                            {ag.gender === "male" ? "Boys Only" : ag.gender === "female" ? "Girls Only" : "Mixed"}
                          </Badge>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAgeGroup(ag.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add New Age Group Form */}
                <div className="p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                  <p className="text-sm font-medium text-white mb-3">
                    {sessionAgeGroups.length === 0 ? "Add Age Group" : "Add Another Age Group"}
                  </p>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Name *</label>
                      <Input
                        value={newAgeGroup.name}
                        onChange={(e) => setNewAgeGroup({...newAgeGroup, name: e.target.value})}
                        placeholder="e.g., U10"
                        className="h-9"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Min Age *</label>
                      <Input
                        type="number"
                        value={newAgeGroup.minAge}
                        onChange={(e) => setNewAgeGroup({...newAgeGroup, minAge: e.target.value})}
                        placeholder="8"
                        min="3"
                        max="18"
                        className="h-9"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Max Age *</label>
                      <Input
                        type="number"
                        value={newAgeGroup.maxAge}
                        onChange={(e) => setNewAgeGroup({...newAgeGroup, maxAge: e.target.value})}
                        placeholder="10"
                        min="3"
                        max="18"
                        className="h-9"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Gender</label>
                      <Select value={newAgeGroup.gender} onValueChange={(v) => setNewAgeGroup({...newAgeGroup, gender: v})}>
                        <SelectTrigger className="h-9">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="mixed">Mixed</SelectItem>
                          <SelectItem value="male">Boys Only</SelectItem>
                          <SelectItem value="female">Girls Only</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  
                  {/* Quick Add Buttons */}
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={addAgeGroup}
                      className="bg-emerald-600 hover:bg-emerald-500/100 text-white border-0"
                    >
                      <Plus className="w-4 h-4 mr-1" />
                      Add Age Group
                    </Button>
                    
                    {/* Quick presets */}
                    <span className="text-xs text-slate-400 self-center ml-2">Quick add:</span>
                    {[
                      { name: "U7", minAge: "5", maxAge: "7" },
                      { name: "U10", minAge: "8", maxAge: "10" },
                      { name: "U12", minAge: "10", maxAge: "12" },
                      { name: "U14", minAge: "12", maxAge: "14" },
                    ].map((preset) => (
                      <Button
                        key={preset.name}
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSessionAgeGroups([...sessionAgeGroups, { ...preset, gender: "mixed", id: Date.now() + Math.random() }]);
                          toast.success(`Added ${preset.name}`);
                        }}
                        className="text-xs"
                      >
                        {preset.name}
                      </Button>
                    ))}
                  </div>
                  
                  {/* Also create for opposite gender option - shows for any boys/girls only selection */}
                  {(sessionAgeGroups.some(ag => ag.gender === "male" || ag.gender === "female") || 
                    (sessionAgeGroups.length === 0 && (sessionGender === "male" || sessionGender === "female"))) && (
                    <div className="mt-3 p-3 bg-gradient-to-r from-blue-500/10 to-pink-500/10 border border-purple-500/30 rounded-lg">
                      <div className="flex items-center gap-2">
                        <Checkbox
                          id="also-both-genders"
                          checked={createForBothGenders}
                          onCheckedChange={setCreateForBothGenders}
                        />
                        <label htmlFor="also-both-genders" className="text-sm font-medium cursor-pointer">
                          <span className="text-blue-400">👦</span> Create <strong>Boys</strong> AND <span className="text-pink-400">👧</span> <strong>Girls</strong> sessions
                        </label>
                      </div>
                      <p className="text-xs text-slate-400 mt-1 ml-6">
                        Automatically creates separate sessions for both genders with the same time/location
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Summary */}
                {sessionAgeGroups.length > 0 && (
                  <div className="mt-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                    <p className="text-sm text-blue-400">
                      📋 <strong>{sessionAgeGroups.length * (createForBothGenders ? 2 : 1)}</strong> session(s) will be created
                      {copyToMultipleDays && selectedDays.length > 0 && (
                        <span> × <strong>{1 + selectedDays.length}</strong> days = <strong>{sessionAgeGroups.length * (createForBothGenders ? 2 : 1) * (1 + selectedDays.length)}</strong> total sessions</span>
                      )}
                    </p>
                    {createForBothGenders && (
                      <p className="text-xs text-slate-400 mt-1">
                        ({sessionAgeGroups.length} age groups × 2 genders = {sessionAgeGroups.length * 2} sessions per day)
                      </p>
                    )}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">Description (Optional)</label>
                <Input
                  value={sessionDescription}
                  onChange={(e) => setSessionDescription(e.target.value)}
                  placeholder="Add any notes about this session"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button onClick={() => setShowSessionDialog(false)} variant="outline">
                  Cancel
                </Button>
                <Button onClick={handleSessionSubmit} className="bg-blue-600 hover:bg-blue-700">
                  {editingSession ? "Update Session" : "Create Session"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Fix this term — dry-run preview → apply */}
        <Dialog open={!!fixTermData} onOpenChange={(o) => { if (!o) setFixTermData(null); }}>
          <DialogContent className="max-w-lg" data-testid="fix-term-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Wrench className="w-5 h-5 text-amber-400" />
                Fix this term — {fixTermData?.termName}
              </DialogTitle>
            </DialogHeader>
            {fixTermData && (
              <div className="space-y-4">
                <p className="text-sm text-slate-400" data-testid="fix-term-message">
                  {fixTermData.message}
                </p>

                {/* BEFORE snapshot */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="rounded-lg border border-slate-700 p-3">
                    <div className="text-xs uppercase text-slate-500 mb-1">Before</div>
                    <ul className="text-sm space-y-1">
                      <li>Sessions: <b>{fixTermData.before?.sessions_total ?? 0}</b></li>
                      <li>Missing team: <b className="text-amber-400" data-testid="fix-term-before-missing-team">{fixTermData.before?.sessions_missing_team_id ?? 0}</b></li>
                      <li>Players on rosters: <b data-testid="fix-term-before-players">{fixTermData.before?.distinct_players_on_rosters ?? 0}</b></li>
                    </ul>
                  </div>
                  {fixTermData.applied ? (
                    <div className="rounded-lg border border-emerald-700 p-3">
                      <div className="text-xs uppercase text-emerald-500 mb-1">After</div>
                      <ul className="text-sm space-y-1">
                        <li>Sessions: <b>{fixTermData.after?.sessions_total ?? 0}</b></li>
                        <li>Missing team: <b className="text-emerald-400" data-testid="fix-term-after-missing-team">{fixTermData.after?.sessions_missing_team_id ?? 0}</b></li>
                        <li>Players on rosters: <b className="text-emerald-400" data-testid="fix-term-after-players">{fixTermData.after?.distinct_players_on_rosters ?? 0}</b></li>
                      </ul>
                    </div>
                  ) : (
                    <div className="rounded-lg border border-sky-700 p-3">
                      <div className="text-xs uppercase text-sky-500 mb-1">Will change</div>
                      <ul className="text-sm space-y-1">
                        <li>Links to remap: <b className="text-sky-400" data-testid="fix-term-links-to-remap">{fixTermData.preview?.links_to_remap ?? 0}</b></li>
                        <li>Teams to stamp: <b className="text-sky-400">{fixTermData.preview?.sessions_missing_team_id ?? 0}</b></li>
                        <li className="text-slate-500 text-xs">Rosters re-synced from current enrolments</li>
                      </ul>
                    </div>
                  )}
                </div>

                {/* Apply result summary */}
                {fixTermData.applied && (
                  <div className="rounded-lg bg-emerald-500/10 border border-emerald-700 p-3 text-sm space-y-1" data-testid="fix-term-result">
                    <div>Links remapped: <b>{fixTermData.repair?.links_remapped ?? 0}</b></div>
                    <div>Session teams stamped: <b>{fixTermData.regenerate?.teams_stamped ?? 0}</b></div>
                    <div>Sessions re-synced: <b>{fixTermData.regenerate?.sessions_updated ?? 0}</b></div>
                    {fixTermData.regenerate?.generation_error && (
                      <div className="text-amber-400 text-xs">Note: new-session generation skipped ({fixTermData.regenerate.generation_error})</div>
                    )}
                  </div>
                )}

                {/* Preview list of affected players (dry-run) */}
                {!fixTermData.applied && (fixTermData.preview?.players?.length > 0) && (
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-slate-700 p-2 text-xs space-y-1" data-testid="fix-term-preview-list">
                    {fixTermData.preview.players.map((p, i) => (
                      <div key={i} className="flex justify-between">
                        <span>{p.player_name || p.name || p.player_id}</span>
                        <span className="text-slate-500">{p.stale_count ?? p.remapped ?? ""} link(s)</span>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex justify-end gap-2 pt-2">
                  <Button variant="outline" size="sm" onClick={() => setFixTermData(null)} data-testid="fix-term-close-btn">
                    {fixTermData.applied ? "Close" : "Cancel"}
                  </Button>
                  {!fixTermData.applied && (
                    <Button
                      size="sm"
                      className="bg-amber-500 hover:bg-amber-600 text-slate-900"
                      onClick={applyFixTerm}
                      disabled={fixTermApplying}
                      data-testid="fix-term-apply-btn"
                    >
                      {fixTermApplying ? "Applying..." : "Apply fix"}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Cancel session date — affected paid players confirm */}
        <Dialog open={!!cancelConfirm} onOpenChange={(o) => { if (!o) setCancelConfirm(null); }}>
          <DialogContent className="max-w-lg bg-[#111827] border-slate-700" data-testid="cancel-credit-confirm">
            <DialogHeader>
              <DialogTitle className="text-white">Cancel {cancelConfirm?.day_name} {cancelConfirm?.date}?</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-300">
                <span className="font-semibold text-amber-400">{cancelConfirm?.affected_count}</span> already-paid player(s) are enrolled on this day. They will each receive an account credit (auto-applied to their next term or ad-hoc booking):
              </p>
              <div className="max-h-56 overflow-y-auto rounded-lg border border-slate-700 divide-y divide-slate-800">
                {(cancelConfirm?.affected_paid_players || []).map((p) => (
                  <div key={p.player_id} className="flex items-center justify-between px-3 py-2 text-sm">
                    <div>
                      <p className="text-white">{p.player_name}</p>
                      <p className="text-xs text-slate-400">{p.parent_name} · {p.parent_email}</p>
                    </div>
                    <span className="font-semibold text-green-400">${(p.credit_amount || 0).toFixed(2)}</span>
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-between px-1">
                <span className="text-sm text-slate-300">Total credits</span>
                <span className="text-lg font-bold text-green-400">${(cancelConfirm?.total_credit || 0).toFixed(2)}</span>
              </div>
              <div className="flex flex-col gap-2 pt-2">
                <Button onClick={() => confirmCancelDate(true)} disabled={cancelBusy} className="bg-green-600 hover:bg-green-500" data-testid="confirm-cancel-with-credits">
                  Cancel date &amp; issue ${(cancelConfirm?.total_credit || 0).toFixed(2)} in credits
                </Button>
                <Button onClick={() => confirmCancelDate(false)} disabled={cancelBusy} variant="outline" className="border-slate-600 text-slate-300" data-testid="confirm-cancel-no-credits">
                  Cancel date only (no credits — I&apos;ll handle it)
                </Button>
                <Button onClick={() => setCancelConfirm(null)} disabled={cancelBusy} variant="ghost" className="text-slate-400">
                  Keep the session
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Price Audit — stored price vs rate table */}
        <Dialog open={showPriceAudit} onOpenChange={setShowPriceAudit}>
          <DialogContent className="max-w-2xl bg-[#111827] border-slate-700 max-h-[85vh] overflow-y-auto" data-testid="price-audit-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">Price Audit — stored price vs rate table</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-xs text-slate-400">
                Flags sessions in <strong>Volume</strong> / <strong>Max-Capacity</strong> terms whose saved Base Price differs from the rate table. This is cosmetic only — billing already uses the rate table — but syncing keeps the displayed numbers consistent. Progressive terms are not shown (their saved price is the billing price).
              </p>
              {auditLoading ? (
                <p className="text-sm text-slate-400 py-6 text-center">Scanning terms…</p>
              ) : !priceAudit ? (
                <p className="text-sm text-slate-400 py-6 text-center">No data.</p>
              ) : priceAudit.clean ? (
                <div className="py-8 text-center" data-testid="audit-clean">
                  <CheckCircle className="w-10 h-10 text-emerald-400 mx-auto mb-2" />
                  <p className="text-emerald-300 font-medium">All clean — every session matches its rate table.</p>
                </div>
              ) : (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-amber-300">{priceAudit.total_flagged} session(s) across {priceAudit.terms.length} term(s)</span>
                    <Button size="sm" onClick={() => syncAudit(null)} disabled={auditBusy} className="bg-amber-600 hover:bg-amber-500" data-testid="audit-sync-all">
                      Sync all to rate table
                    </Button>
                  </div>
                  <div className="space-y-3">
                    {priceAudit.terms.map((t) => (
                      <div key={t.term_id} className="p-3 bg-[#0a0f1a] rounded-lg border border-slate-700" data-testid={`audit-term-${t.term_id}`}>
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="text-sm font-medium text-white">{t.term_name}</p>
                            <p className="text-xs text-slate-400">
                              {t.rate_table_name} · <span className="uppercase">{t.pricing_mode}</span> · rate <span className="text-emerald-400">${t.rate_price}</span>
                              {t.is_finalized && <span className="ml-2 text-amber-400">finalized</span>}
                            </p>
                            <p className="text-xs text-slate-500 mt-1">
                              {t.mismatch_count} session(s) stored at: {[...new Set(t.mismatched_sessions.map(m => `$${m.stored_price}`))].join(", ")}
                            </p>
                          </div>
                          <Button size="sm" variant="outline" onClick={() => syncAudit(t.term_id)} disabled={auditBusy} className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10 flex-shrink-0">
                            Sync
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
